// frontend/src/components/Pipeline/NbaCard.jsx
// Copiloto "Próxima melhor ação" (Slice E1): muestra la última recomendación IA
// persistida, marca staleness, y ejecuta en un clic. Falla suave: nunca rompe la ficha.
import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { SparklesIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import toast from 'react-hot-toast';

const ACTION_LABEL = { send_hsm: 'Enviar WhatsApp (SDR)', create_task: 'Criar tarefa', wait: 'Aguardar' };
const HSM_LABEL = {
  iniciacao: 'Iniciar conversa', retomar: 'Retomar conversa',
  agendar: 'Empurrar agendamento', noshow: 'Reativar pós no-show',
};

function actionTitle(rec) {
  if (rec.action === 'send_hsm') return `${ACTION_LABEL.send_hsm}: ${HSM_LABEL[rec.params?.templateKey] || rec.params?.templateKey || ''}`;
  if (rec.action === 'create_task') return `${ACTION_LABEL.create_task}: ${rec.params?.title || ''}`;
  if (rec.action === 'wait') return `${ACTION_LABEL.wait} ${rec.params?.until_days ?? '?'} dia(s)`;
  return rec.action;
}

export default function NbaCard({ opportunityId }) {
  const [computing, setComputing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery(
    ['nba', opportunityId],
    async () => (await api.get(`/opportunities/${opportunityId}/nba`)).data.data,
    { staleTime: 60000, enabled: !!opportunityId },
  );

  const compute = async () => {
    setComputing(true);
    try {
      const res = await api.post(`/opportunities/${opportunityId}/nba/compute`);
      if (res.data.success) {
        queryClient.setQueryData(['nba', opportunityId], res.data.data);
        toast.success('Sugestão pronta');
      } else toast.error(res.data.error || 'IA indisponível, tente de novo');
    } catch (e) {
      toast.error(e.response?.data?.error || 'IA indisponível, tente de novo');
    } finally { setComputing(false); }
  };

  const execute = async (rec) => {
    setExecuting(true);
    try {
      const res = await api.post(`/opportunities/${opportunityId}/nba/${rec.id}/execute`);
      if (res.data.success) {
        toast.success(res.data.data.degraded ? 'WhatsApp indisponível — tarefa criada no lugar' : 'Ação executada');
        queryClient.invalidateQueries(['nba', opportunityId]);
        queryClient.invalidateQueries(['opp-timeline', opportunityId]);
      } else toast.error(res.data.error || 'Falha ao executar');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Falha ao executar');
    } finally { setExecuting(false); }
  };

  const copyDraft = (text) => {
    if (!navigator.clipboard) { toast.error('Não foi possível copiar'); return; }
    navigator.clipboard.writeText(text).then(
      () => toast.success('Copiado'),
      () => toast.error('Não foi possível copiar'),
    );
  };

  if (isLoading) {
    return (
      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <div className="h-5 bg-gray-100 animate-pulse rounded w-2/3" />
      </section>
    );
  }

  const rec = data?.recommendation;
  const stale = data?.stale;

  return (
    <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
      <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <SparklesIcon aria-hidden="true" className="h-5 w-5 text-indigo-600" /> Próxima melhor ação (IA)
      </h2>

      {isError && <p className="text-sm text-red-600 mb-2">Indisponível — tente de novo.</p>}

      {rec && (
        <>
          <p className="text-sm font-medium text-gray-800 mb-1">{actionTitle(rec)}</p>
          <p className="text-sm text-gray-600 bg-gray-50 rounded p-3 whitespace-pre-wrap mb-2">{rec.reasoning}</p>
          {rec.action === 'create_task' && rec.params?.description && (
            <div className="mb-2">
              <div className="text-sm text-gray-600 bg-indigo-50 rounded p-3 whitespace-pre-wrap">{rec.params.description}</div>
              <button type="button" onClick={() => copyDraft(rec.params.description)} className="text-xs text-indigo-600 mt-1">
                Copiar conteúdo
              </button>
            </div>
          )}
          <p className="text-xs text-gray-400 mb-2">
            Computada em {rec.created_at ? new Date(rec.created_at).toLocaleString('pt-BR') : '—'}
            {rec.status === 'executed' && ' · já executada'}
          </p>
          {stale && rec.status === 'suggested' && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded p-2 mb-2">
              Desatualizada — o lead teve atividade depois desta sugestão.
            </p>
          )}
          {rec.status === 'suggested' && rec.action !== 'wait' && (
            <button type="button" onClick={() => execute(rec)} disabled={executing}
              className="w-full bg-indigo-600 text-white px-3 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50 text-sm mb-2">
              {executing ? 'Executando…' : 'Executar'}
            </button>
          )}
        </>
      )}

      <button type="button" onClick={compute} disabled={computing}
        className="w-full border border-indigo-300 text-indigo-700 px-3 py-2 rounded-md hover:bg-indigo-50 disabled:opacity-50 text-sm">
        {computing ? 'Pensando… (~10s)' : (rec ? 'Recalcular' : 'Sugerir próxima ação')}
      </button>
    </section>
  );
}
